/**
 * Filter UI
 * Update filter UI based on state and handle user interactions
 */

import { state } from '../core/state.js';
import { elements } from '../core/elements.js';
import { mergeFacetKeys } from '../utils/facet-utils.js';
import { normalizeBrands } from '../utils/brand-utils.js';
import {
  getSelectedGender,
  resetGenderFilterCheckboxesIfNeeded,
} from '../filters/gender-filter.js';
import { isEmbeddedMode } from '../modes/embedded.js';

// Will be injected by index.js
let handleFilterChange = null;
let performSearchAuto = null;

const isDesktop = window.innerWidth >= 1280;

// Tracks items pinned from popup to the visible list — persists until page reload
const pinnedFromPopup = new Map(); // filterType → Set of values

const TYPE_DISPLAY_NAMES = {
  OVERNIGHT: 'Overnight',
  EXTENDED: 'Extended Day',
  DAY: 'Full Day',
  HALFDAY: 'Half Day',
  CLINIC: 'Clinic',
};

// Canonical skill order — fixed regardless of Algolia count/geo
const SKILLS_ORDER = [
  'All Abilities',
  'Beginner',
  'Intermediate',
  'Advanced',
  'Position/Skill Specialty',
  'ID / Showcase',
  'Adult',
];

/**
 * Set dependencies
 */
export function setDependencies(deps) {
  handleFilterChange = deps.handleFilterChange;
  performSearchAuto = deps.performSearchAuto;
}

/**
 * Update filters count badge
 */
export function updateFiltersCount() {
  const brandCount = state.brandPageMode
    ? (state.brandPageBrandSelected ? 1 : 0) + (state.brandPageAllOthers ? 1 : 0)
    : state.brand.length;

  const count =
    (state.live === true ? 1 : 0) +
    state.types.length +
    (state.filterCoed ? 1 : 0) +
    (state.filterMaleOnly ? 1 : 0) +
    (state.filterFemaleOnly ? 1 : 0) +
    brandCount +
    state.skills.length;

  // Mobile Filter button badge
  if (elements.filtersCount) {
    if (count > 0) {
      elements.filtersCount.textContent = count;
      elements.filtersCount.style.display = 'flex';
    } else {
      elements.filtersCount.style.display = 'none';
    }
  }

  // Sidebar header labels "Filters (N)" / "Filter (N)"
  if (elements.sidebarFiltersCounts && elements.sidebarFiltersCounts.length) {
    elements.sidebarFiltersCounts.forEach((el) => {
      el.textContent = `(${count})`;
    });
  }
}

/**
 * Update filter UI
 * Main function to update all filter controls based on current state
 */
export function updateFilterUI() {
  // Sport and Age filters are removed from sidebar
  // They are only in the main search form

  // Use facets from state (already recalculated from filtered results in performSearch)
  elements.liveCheckbox.checked = state.live === true;

  // Types: merge with allFacets to keep options stable
  const computedTypesFacets = mergeFacetKeys(
    state.allFacets.feeCodes,
    state.facets.types
  );
  updateCheckboxGroup(
    elements.typeFilters,
    'types',
    computedTypesFacets,
    state.types
  );

  // Reset checkboxes if selectedGender changed
  resetGenderFilterCheckboxesIfNeeded();
  updateGenderFilters();

  // In embedded mode: always hide brand block (preset brands still filter silently via state.brand)
  const brandContainer = elements.brandFilters?.closest('.rd-search-results__filter');
  if (brandContainer) {
    brandContainer.style.display = isEmbeddedMode() ? 'none' : '';
  }

  // Skills: merge with allFacets to keep options stable (always render, even in brand page mode)
  const computedSkillsFacets = mergeFacetKeys(
    state.allFacets.skills,
    state.facets.skills
  );
  updateCheckboxGroup(
    elements.skillFilters,
    'skills',
    computedSkillsFacets,
    state.skills
  );

  // Brand page mode: show only 2 checkboxes — the specific brand and "All other brands"
  if (state.brandPageMode && state.brandPageBrand) {
    updateBrandPageCheckboxes(elements.brandFilters);
    return;
  }

  // Brands: merge with allFacets to keep options stable (Algolia 'brands' array)
  const computedBrandFacets = mergeFacetKeys(
    state.allFacets.brands,
    state.facets.brands
  );
  // Normalize selected brands after facets are loaded
  state.brand = normalizeBrands(state.brand);

  updateCheckboxGroup(
    elements.brandFilters,
    'brands',
    computedBrandFacets,
    state.brand
  );

  elements.liveCheckbox.closest('.rd-search-results__filter-label').classList.remove('rd-skeleton');
}

/**
 * Render 2-checkbox brand sidebar for brand page mode.
 * Shows the specific brand (checked when selected) and "All other brands" (checked when no brand filter).
 */
function updateBrandPageCheckboxes(container) {
  if (!container) return;

  const brandName = state.brandPageBrand;
  const isBrandSelected = state.brandPageBrandSelected;
  const isAllOthersSelected = state.brandPageAllOthers;

  // Check if the DOM is already in sync to avoid unnecessary rebuilds
  const existingCheckboxes = container.querySelectorAll('input[type="checkbox"][data-filter="brands"]');
  if (existingCheckboxes.length === 2) {
    const [brandCb, otherCb] = existingCheckboxes;
    brandCb.checked = isBrandSelected;
    otherCb.checked = isAllOthersSelected;
    return;
  }

  container.innerHTML = '';

  // Checkbox 1: the specific brand
  const brandLabel = document.createElement('label');
  brandLabel.className = 'rd-search-results__filter-label caption font-regular show';
  const brandCheckbox = document.createElement('input');
  brandCheckbox.type = 'checkbox';
  brandCheckbox.className = 'show rd-checkbox';
  brandCheckbox.name = 'brands';
  brandCheckbox.value = brandName;
  brandCheckbox.dataset.filter = 'brands';
  brandCheckbox.checked = isBrandSelected;
  brandLabel.appendChild(brandCheckbox);
  const { fieldName: brandFieldName } = createCheckboxFieldInnerEls(brandName);
  brandLabel.appendChild(brandFieldName);
  container.appendChild(brandLabel);

  // Checkbox 2: "All other brands"
  const otherLabel = document.createElement('label');
  otherLabel.className = 'rd-search-results__filter-label caption font-regular show';
  const otherCheckbox = document.createElement('input');
  otherCheckbox.type = 'checkbox';
  otherCheckbox.className = 'show rd-checkbox';
  otherCheckbox.name = 'brands';
  otherCheckbox.value = '_all_other_brands';
  otherCheckbox.dataset.filter = 'brands';
  otherCheckbox.checked = isAllOthersSelected;
  otherLabel.appendChild(otherCheckbox);
  const { fieldName: otherFieldName } = createCheckboxFieldInnerEls('All other brands');
  otherLabel.appendChild(otherFieldName);
  container.appendChild(otherLabel);

  // Attach event listeners
  if (handleFilterChange) {
    container.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.addEventListener('change', handleFilterChange);
    });
  }
}

/**
 * Update gender filters with new logic:
 * Always show 3 checkboxes: Coed, Male only, Female only
 * Checkboxes are unchecked by default and don't auto-check when selectedGender changes
 * Filter is always visible
 */
export function updateGenderFilters() {
  if (!elements.genderFilters) return;

  elements.genderFilters.innerHTML = '';

  const selectedGender = getSelectedGender();

  // 1. Coed checkbox
  const coedLabel = document.createElement('label');
  coedLabel.className =
    'rd-search-results__filter-label caption font-regular show';

  const coedCheckbox = document.createElement('input');
  coedCheckbox.type = 'checkbox';
  coedCheckbox.className = 'show rd-checkbox';
  coedCheckbox.name = 'genderFilter';
  coedCheckbox.value = 'coed';
  coedCheckbox.dataset.filter = 'genderFilter';
  coedCheckbox.checked = state.filterCoed;

  coedLabel.appendChild(coedCheckbox);
  const { fieldName } = createCheckboxFieldInnerEls('Co-ed');
  coedLabel.appendChild(fieldName);
  elements.genderFilters.appendChild(coedLabel);

  // 2. Male only checkbox (hide if selectedGender is female)
  if (selectedGender !== 'female') {
    const maleOnlyLabel = document.createElement('label');
    maleOnlyLabel.className =
      'rd-search-results__filter-label caption font-regular show';

    const maleOnlyCheckbox = document.createElement('input');
    maleOnlyCheckbox.type = 'checkbox';
    maleOnlyCheckbox.className = 'show rd-checkbox';
    maleOnlyCheckbox.name = 'genderFilter';
    maleOnlyCheckbox.value = 'maleOnly';
    maleOnlyCheckbox.dataset.filter = 'genderFilter';
    maleOnlyCheckbox.checked = state.filterMaleOnly;

    maleOnlyLabel.appendChild(maleOnlyCheckbox);
    const { fieldName: maleNameEl } = createCheckboxFieldInnerEls('Male only');
    maleOnlyLabel.appendChild(maleNameEl);
    elements.genderFilters.appendChild(maleOnlyLabel);
  }

  // 3. Female only checkbox (hide if selectedGender is male)
  if (selectedGender !== 'male') {
    const femaleOnlyLabel = document.createElement('label');
    femaleOnlyLabel.className =
      'rd-search-results__filter-label caption font-regular show';

    const femaleOnlyCheckbox = document.createElement('input');
    femaleOnlyCheckbox.type = 'checkbox';
    femaleOnlyCheckbox.className = 'show rd-checkbox';
    femaleOnlyCheckbox.name = 'genderFilter';
    femaleOnlyCheckbox.value = 'femaleOnly';
    femaleOnlyCheckbox.dataset.filter = 'genderFilter';
    femaleOnlyCheckbox.checked = state.filterFemaleOnly;

    femaleOnlyLabel.appendChild(femaleOnlyCheckbox);
    const { fieldName: femaleNameEl } =
      createCheckboxFieldInnerEls('Female only');
    femaleOnlyLabel.appendChild(femaleNameEl);
    elements.genderFilters.appendChild(femaleOnlyLabel);
  }

  // Add event listeners
  if (handleFilterChange) {
    elements.genderFilters
      .querySelectorAll('input[type="checkbox"]')
      .forEach((checkbox) => {
        checkbox.addEventListener('change', handleFilterChange);
      });
  }

  // Log final state of all checkboxes
  const allCheckboxes = elements.genderFilters.querySelectorAll(
    'input[type="checkbox"]'
  );
  const checkboxStates = Array.from(allCheckboxes).map((cb) => ({
    value: cb.value,
    checked: cb.checked,
  }));
  console.log('✅ Final checkbox states:', checkboxStates);
}

/**
 * Create checkbox inner elements (name only, no count).
 */
function createCheckboxFieldInnerEls(displayValue = '') {
  const fieldName = document.createElement('span');
  fieldName.className = 'rd-search-results__filter-field-name capsize';
  fieldName.textContent = displayValue;
  return { fieldName };
}

/**
 * Render checkbox group with popup for desktop
 */
function renderCheckboxGroup(container, allLabels, filterType) {
  if (!container) return;

  if (!isDesktop) {
    for (const label of allLabels) {
      container.appendChild(label);
    }
    return;
  }

  // Divide allLabels into groups:
  // 1. First 6 items + selected items from remaining (moved to pos 7+)
  // 2. Remaining unselected items divided into arrays of 10-15 items each
  const firstGroup = allLabels.slice(0, 6);
  const remainingItems = allLabels.slice(6);

  if (!pinnedFromPopup.has(filterType)) {
    pinnedFromPopup.set(filterType, new Set());
  }
  const pinned = pinnedFromPopup.get(filterType);
  for (const label of remainingItems) {
    const cb = label.querySelector('input[type="checkbox"]');
    if (cb && cb.checked) {
      pinned.add(cb.value);
    }
  }

  const remainingByValue = new Map();
  for (const label of remainingItems) {
    const cb = label.querySelector('input[type="checkbox"]');
    if (cb) remainingByValue.set(cb.value, label);
  }

  const selectedFromRemaining = [];
  for (const value of pinned) {
    const label = remainingByValue.get(value);
    if (label) selectedFromRemaining.push(label.cloneNode(true));
  }

  const visibleGroup = [...firstGroup, ...selectedFromRemaining];
  const remainingGroups = [];

  const groupSize = remainingItems.length > 30 ? 15 : 10;

  for (let i = 0; i < remainingItems.length; i += groupSize) {
    remainingGroups.push(remainingItems.slice(i, i + groupSize));
  }

  for (const label of visibleGroup) {
    container.appendChild(label);
  }

  if (remainingGroups.length > 0) {
    const popup = document.createElement('div');
    popup.className = 'rd-search-results__filter-fields-popup';

    for (const group of remainingGroups) {
      const groupElement = document.createElement('div');
      groupElement.className = 'rd-search-results__filter-fields-popup-group';
      for (const label of group) {
        groupElement.appendChild(label);
      }
      popup.appendChild(groupElement);
    }
    container.appendChild(popup);
    const btn = document.createElement('button');
    btn.className =
      'rd-search-results__filter-fields-popup-btn btn btn_link btn_link_small';
    let btnText = 'View all';
    switch (filterType) {
      case 'sportSlug':
        btnText = 'View all sports';
        break;
      case 'types':
        btnText = 'View all types';
        break;
      case 'gender':
        btnText = 'View all genders';
        break;
      case 'brands':
        btnText = 'View more brands';
        break;
      case 'skills':
        btnText = 'View more skills';
        break;
    }
    btn.textContent = btnText;

    // Prevent popup clicks from closing it
    popup.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    container.appendChild(btn);
  }
}

/**
 * Update checkbox group
 */
function updateCheckboxGroup(container, filterType, facets, selectedValues) {
  if (!container) return;

  let allFacets = Object.entries(facets || {});

  for (const value of selectedValues) {
    if (filterType === 'types' && value === '_empty') continue;
    const valueInFacets =
      filterType === 'brands' || filterType === 'types'
        ? facets &&
          Object.keys(facets).some(
            (k) => k.toLowerCase() === value.toLowerCase()
          )
        : facets && typeof facets[value] !== 'undefined';

    if (!valueInFacets) {
      allFacets.push([value, 0]);
    }
  }

  // Hide _empty from types list (index placeholder for camps with no feeCodes)
  if (filterType === 'types') {
    allFacets = allFacets.filter(([v]) => v !== '_empty');
  }

  const newValues = allFacets.map(([v]) => v);
  const checkboxes = container.querySelectorAll(
    'input[type="checkbox"][data-filter="' + filterType + '"]'
  );
  const currentValues = [...checkboxes].map((cb) => cb.value);

  const setEqual = (a, b) => {
    if (a.length !== b.length) return false;
    if (filterType === 'brands') {
      const sa = new Set(a.map((x) => (x || '').toLowerCase()));
      const sb = new Set(b.map((x) => (x || '').toLowerCase()));
      return sa.size === sb.size && [...sa].every((x) => sb.has(x));
    }
    const sa = new Set(a);
    return sa.size === new Set(b).size && [...sa].every((x) => b.includes(x));
  };

  // Option set unchanged: only update .checked, do not touch DOM — popup stays open, no flicker
  // Exception: if a selected item lives in the popup, force a full rebuild so it moves to the visible list
  if (checkboxes.length > 0 && setEqual(newValues, currentValues)) {
    const popup = container.querySelector('.rd-search-results__filter-fields-popup');
    const hasSelectedInPopup = popup && [...popup.querySelectorAll('input[type="checkbox"]')].some((cb) =>
      filterType === 'brands' || filterType === 'types'
        ? selectedValues.some((sv) => (sv || '').toLowerCase() === (cb.value || '').toLowerCase())
        : selectedValues.includes(cb.value)
    );

    if (!hasSelectedInPopup) {
      checkboxes.forEach((cb) => {
        cb.checked =
          filterType === 'brands' || filterType === 'types'
            ? selectedValues.some(
                (sv) =>
                  (sv || '').toLowerCase() === (cb.value || '').toLowerCase()
              )
            : selectedValues.includes(cb.value);
      });
      return;
    }
  }

  // Full rebuild: preserve and restore popup open state
  const wasPopupOpen = !!container.querySelector(
    '.rd-search-results__filter-fields-popup.open'
  );
  container.innerHTML = '';

  const allLabels = [];

  // Sort: for types use TYPE_DISPLAY_NAMES key order; for skills use SKILLS_ORDER;
  // otherwise keep Algolia's count-based order, only move "Other" to the end
  allFacets.sort((a, b) => {
    if (a[0] === 'Other') return 1;
    if (b[0] === 'Other') return -1;
    if (filterType === 'types') {
      const order = Object.keys(TYPE_DISPLAY_NAMES);
      const idxA = order.indexOf(a[0].toUpperCase());
      const idxB = order.indexOf(b[0].toUpperCase());
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    }
    if (filterType === 'skills') {
      const idxA = SKILLS_ORDER.indexOf(a[0]);
      const idxB = SKILLS_ORDER.indexOf(b[0]);
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    }
    return 0;
  });

  allFacets.forEach(([value]) => {
    const label = document.createElement('label');
    label.className =
      'rd-search-results__filter-label caption font-regular show';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'show rd-checkbox';
    checkbox.name = filterType;
    checkbox.value = value;
    checkbox.dataset.filter = filterType;
    // Case-insensitive comparison for brands and types
    checkbox.checked =
      filterType === 'brands' || filterType === 'types'
        ? selectedValues.some(
            (sv) => sv.toLowerCase() === value.toLowerCase()
          )
        : selectedValues.includes(value);

    // Format label based on filter type
    let displayValue = value;
    if (filterType === 'sportSlug') {
      displayValue = value.charAt(0).toUpperCase() + value.slice(1);
    } else if (filterType === 'types') {
      displayValue =
        TYPE_DISPLAY_NAMES[value.toUpperCase()] ||
        (value.charAt(0).toUpperCase() + value.slice(1).toLowerCase());
    } else if (filterType === 'gender') {
      displayValue =
        value === 'all'
          ? 'Co-ed'
          : value.charAt(0).toUpperCase() + value.slice(1);
    } else if (filterType === 'brands') {
      displayValue = value; // Keep brand name as-is (e.g., "Nike", "US Sports Camps")
    }

    label.appendChild(checkbox);
    const { fieldName } = createCheckboxFieldInnerEls(displayValue);
    label.appendChild(fieldName);

    allLabels.push(label);
  });

  renderCheckboxGroup(container, allLabels, filterType);

  // Restore popup open state after re-render (filter change triggers full rebuild)
  if (wasPopupOpen) {
    const popup = container.querySelector(
      '.rd-search-results__filter-fields-popup'
    );
    if (popup) popup.classList.add('open');
  }

  // Add event listeners
  if (handleFilterChange) {
    container.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.addEventListener('change', handleFilterChange);
    });
  }
}

/**
 * Update active filters display
 */
export function updateActiveFilters() {
  if (!elements.activeFilters) return;

  elements.activeFilters.innerHTML = '';

  const activeFilters = [];

  if (state.zip) {
    activeFilters.push({ type: 'zip', value: state.zip, label: state.zip });
  }
  if (state.sportSlug.length) {
    state.sportSlug.forEach((slug) => {
      activeFilters.push({ type: 'sportSlug', value: slug, label: slug });
    });
  }
  // Add other filters...

  activeFilters.forEach((filter) => {
    const item = document.createElement('a');
    item.href = '#';
    item.className = 'item';
    item.innerHTML = `
                <span>${filter.label}</span>
                <img class="bc-close" src="/themes/v2/img/close-x.svg">
            `;
    item.addEventListener('click', (e) => {
      e.preventDefault();
      removeFilter(filter.type, filter.value);
    });
    elements.activeFilters.appendChild(item);
  });
}

/**
 * Remove filter
 */
export function removeFilter(type, value) {
  if (type === 'zip') {
    state.zip = '';
    state.coordinates = null;
    state.isStateSearch = false;
    state.searchStateCode = null;
    state.searchStateName = null;
    state.isExcludeUSSearch = false;
    state.excludeUSRegionLabel = null;
    state.regionFilter = null;
    state.ukStateValue = null;
    state.ukCountryFilter = null;
    state.sortByDistanceNoRadius = false;
    // Reset unrecognized location banner flag when location is cleared
    state.showUnrecognizedLocationBanner = false;
    state.unrecognizedLocationBannerContent = null;
    if (elements.zipInput) elements.zipInput.value = '';
    if (elements.mobileZipInput) elements.mobileZipInput.value = '';
  } else if (type === 'sportSlug') {
    state.sportSlug = state.sportSlug.filter((v) => v !== value);
  } else if (type === 'types') {
    state.types = state.types.filter((v) => v !== value);
  } else if (type === 'gender') {
    state.gender = state.gender.filter((v) => v !== value);
  } else if (type === 'brand' || type === 'brands') {
    // Remove by case-insensitive comparison
    state.brand = state.brand.filter(
      (v) => v.toLowerCase() !== value.toLowerCase()
    );
  }

  if (performSearchAuto) performSearchAuto();
}

console.log('📦 Filter UI module loaded');
